import type { CSSProperties } from 'react';
import type { BorderStyle, CornerRadius, Padding } from "./types";

export type ColumnStackDividerRole = 'below' | 'above';

export function columnStackDividerRole(
  column: { border: BorderStyle },
  columnIndex: number,
  columnCount: number,
): ColumnStackDividerRole | null {
  if (columnCount < 2 || column.border.style === 'none') {
    return null;
  }
  const { width } = column.border;
  if (columnIndex < columnCount - 1 && width.right > 0) {
    return 'below';
  }
  if (columnIndex > 0 && width.left > 0) {
    return 'above';
  }
  return null;
}

export function columnHasStackDividers(
  column: { border: BorderStyle },
  columnIndex: number,
  columnCount: number,
): boolean {
  if (columnCount < 2 || column.border.style === 'none') {
    return false;
  }
  const { width } = column.border;
  return (
    (columnIndex < columnCount - 1 && width.right > 0) ||
    (columnIndex > 0 && width.left > 0)
  );
}

export const EMAIL_COL_CLASS = 'email-col';
export const EMAIL_COL_GAP_CLASS = 'email-col-gap';
export const EMAIL_COL_INNER_RIGHT_CLASS = 'email-col-inner-right';
export const EMAIL_COL_INNER_LEFT_CLASS = 'email-col-inner-left';
export const EMAIL_COL_DIVIDER_BELOW_CLASS = 'email-col-divider-below';
export const EMAIL_COL_DIVIDER_ABOVE_CLASS = 'email-col-divider-above';
export const EMAIL_SURFACE_CELL_CLASS = 'email-surface-cell';

export function columnResponsiveClasses(
  column: { border: BorderStyle },
  columnIndex: number,
  columnCount: number,
): string[] {
  const classes = [EMAIL_COL_CLASS];
  if (columnCount > 1) {
    if (columnIndex < columnCount - 1) {
      classes.push(EMAIL_COL_INNER_RIGHT_CLASS);
    }
    if (columnIndex > 0) {
      classes.push(EMAIL_COL_INNER_LEFT_CLASS);
    }
  }
  if (column.border.style !== 'none') {
    const { width } = column.border;
    if (columnIndex < columnCount - 1 && width.right > 0) {
      classes.push(EMAIL_COL_DIVIDER_BELOW_CLASS);
    }
    if (columnIndex > 0 && width.left > 0) {
      classes.push(EMAIL_COL_DIVIDER_ABOVE_CLASS);
    }
  }
  return classes;
}

export const TABLE_RESET: CSSProperties = {
  borderCollapse: 'collapse',
  borderSpacing: 0,
  msoTableLspace: '0pt',
  msoTableRspace: '0pt',
} as CSSProperties;

export function padStyle(padding: Padding): CSSProperties {
  return {
    paddingTop: padding.top,
    paddingRight: padding.right,
    paddingBottom: padding.bottom,
    paddingLeft: padding.left,
    boxSizing: 'border-box',
  };
}

export function radiusCss(radius: CornerRadius): string | undefined {
  const { topLeft, topRight, bottomRight, bottomLeft } = radius;
  if (!topLeft && !topRight && !bottomRight && !bottomLeft) {
    return undefined;
  }
  if (
    topLeft === topRight &&
    topRight === bottomRight &&
    bottomRight === bottomLeft
  ) {
    return `${topLeft}px`;
  }
  return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
}

export function radiusStyle(radius: CornerRadius): CSSProperties {
  const css = radiusCss(radius);
  if (!css) {
    return {};
  }
  return { borderRadius: css };
}

function borderSideCss(
  width: number,
  style: BorderStyle['style'],
  color: string,
): string | undefined {
  if (width <= 0 || style === 'none') {
    return undefined;
  }
  return `${width}px ${style} ${color}`;
}

export function borderStyle(border: BorderStyle): CSSProperties {
  const { width, style, color } = border;
  const result: CSSProperties = {};
  const top = borderSideCss(width.top, style, color);
  const right = borderSideCss(width.right, style, color);
  const bottom = borderSideCss(width.bottom, style, color);
  const left = borderSideCss(width.left, style, color);
  if (top) {
    result.borderTop = top;
  }
  if (right) {
    result.borderRight = right;
  }
  if (bottom) {
    result.borderBottom = bottom;
  }
  if (left) {
    result.borderLeft = left;
  }
  if (top || right || bottom || left) {
    result.boxSizing = "border-box";
  }
  return result;
}

function hasVisibleBorder(border: BorderStyle): boolean {
  if (border.style === "none") {
    return false;
  }
  const { width } = border;
  return width.top > 0 || width.right > 0 || width.bottom > 0 || width.left > 0;
}

export function bandBorderInsetShadow(border: BorderStyle): string | undefined {
  const { width, style, color } = border;
  if (style === "none") {
    return undefined;
  }

  const { top, right, bottom, left } = width;
  if (top <= 0 && right <= 0 && bottom <= 0 && left <= 0) {
    return undefined;
  }

  if (top === right && right === bottom && bottom === left) {
    return `inset 0 0 0 ${top}px ${color}`;
  }

  const shadows: string[] = [];
  if (top > 0) {
    shadows.push(`inset 0 ${top}px 0 0 ${color}`);
  }
  if (right > 0) {
    shadows.push(`inset -${right}px 0 0 0 ${color}`);
  }
  if (bottom > 0) {
    shadows.push(`inset 0 -${bottom}px 0 0 ${color}`);
  }
  if (left > 0) {
    shadows.push(`inset ${left}px 0 0 0 ${color}`);
  }

  return shadows.length > 0 ? shadows.join(", ") : undefined;
}

const SURFACE_BORDER_TABLE_RESET: CSSProperties = {
  borderCollapse: 'separate',
  borderSpacing: 0,
  width: '100%',
} as CSSProperties;

export function surfaceBorderTableStyle(
  border: BorderStyle,
  radius: CornerRadius,
): CSSProperties {
  return {
    ...SURFACE_BORDER_TABLE_RESET,
    ...radiusStyle(radius),
    ...borderStyle(border),
  };
}

export function surfaceContentClipStyle(
  radius: CornerRadius,
  editorMode = false,
): CSSProperties {
  if (editorMode || radiusCss(radius) === undefined) {
    return {};
  }
  return {
    ...radiusStyle(radius),
    overflow: 'hidden',
  };
}

export function surfaceGroupContentClipStyle(
  radius: CornerRadius,
): CSSProperties {
  if (radiusCss(radius) === undefined) {
    return {};
  }
  return {
    ...radiusStyle(radius),
    overflow: 'hidden',
  };
}

export function rowEdgeColumnRadiusStyle(
  rowRadius: CornerRadius,
  columnRadius: CornerRadius,
  columnIndex: number,
  columnCount: number,
  editorMode = false,
): CSSProperties {
  if (
    !editorMode ||
    radiusCss(rowRadius) === undefined ||
    radiusCss(columnRadius) !== undefined
  ) {
    return {};
  }

  if (columnCount === 1) {
    return radiusStyle(rowRadius);
  }

  const corners: CornerRadius = {
    topLeft: 0,
    topRight: 0,
    bottomRight: 0,
    bottomLeft: 0,
  };
  if (columnIndex === 0) {
    corners.topLeft = rowRadius.topLeft;
    corners.bottomLeft = rowRadius.bottomLeft;
  }
  if (columnIndex === columnCount - 1) {
    corners.topRight = rowRadius.topRight;
    corners.bottomRight = rowRadius.bottomRight;
  }
  return radiusStyle(corners);
}

export function surfaceCellBorderStyle(
  border: BorderStyle,
  radius: CornerRadius,
  editorMode = false,
): CSSProperties {
  const radiusValue = radiusCss(radius);
  const insetShadow =
    radiusValue !== undefined && hasVisibleBorder(border)
      ? bandBorderInsetShadow(border)
      : undefined;

  return {
    ...radiusStyle(radius),
    ...(radiusValue && !editorMode ? { overflow: "hidden" as const } : {}),
    ...(insetShadow
      ? { boxSizing: "border-box", boxShadow: insetShadow }
      : borderStyle(border)),
  };
}

export function surfaceCellClassName(
  radius: CornerRadius,
  editorMode = false,
): string | undefined {
  if (editorMode || radiusCss(radius) === undefined) {
    return undefined;
  }
  return EMAIL_SURFACE_CELL_CLASS;
}

export function presentationTableProps() {
  return {
    role: 'presentation' as const,
    cellPadding: 0,
    cellSpacing: 0,
    border: 0,
    style: { ...TABLE_RESET, width: '100%' },
  };
}

export function parseHexColor(
  color: string,
): { r: number; g: number; b: number } | null {
  const trimmed = color.trim();
  if (trimmed === 'transparent') {
    return null;
  }
  const hex = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map(channel => channel + channel)
          .join('')
      : hex.length === 6
        ? hex
        : null;
  if (!full) {
    return null;
  }
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return null;
  }
  return { r, g, b };
}

export function isDarkColor(color: string): boolean {
  const rgb = parseHexColor(color);
  if (!rgb) {
    return false;
  }
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance < 0.45;
}

function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const channels = [rgb.r, rgb.g, rgb.b].map(channel => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

export function contrastRatio(
  foreground: string,
  background: string,
): number | null {
  const fg = parseHexColor(foreground);
  const bg = parseHexColor(background);
  if (!fg || !bg) {
    return null;
  }
  const lighter = Math.max(relativeLuminance(fg), relativeLuminance(bg));
  const darker = Math.min(relativeLuminance(fg), relativeLuminance(bg));
  return (lighter + 0.05) / (darker + 0.05);
}

export function meetsContrastAA(
  foreground: string,
  background: string,
  largeText = false,
): boolean {
  const ratio = contrastRatio(foreground, background);
  if (ratio === null) {
    return true;
  }
  return ratio >= (largeText ? 3 : 4.5);
}

function isOpaqueSurfaceColor(color: string | undefined): color is string {
  return color != null && color !== "transparent";
}

export function resolveBlockSurfaceColor(
  columnBackground: string,
  rowBackground: string,
  groupBackground: string | undefined,
  contentBackground: string,
): string {
  if (isOpaqueSurfaceColor(columnBackground)) {
    return columnBackground;
  }
  if (isOpaqueSurfaceColor(rowBackground)) {
    return rowBackground;
  }
  if (isOpaqueSurfaceColor(groupBackground)) {
    return groupBackground;
  }
  return contentBackground;
}

export function resolveEditorRowSurfaceColor(
  rowBackground: string,
  groupBackground: string | undefined,
  contentBackground: string,
): string {
  return resolveBlockSurfaceColor(
    "transparent",
    rowBackground,
    groupBackground,
    contentBackground,
  );
}
